import './style.scss'
import { Checkbox, Dropdown, Input, message, Modal, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { FC, useRef, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import { useNavigate } from 'react-router-dom'
import * as Api from '@/api/home'
import AudioBg from '@/assets/audio-bg.webp'
import IconMusic from '@/assets/icon-music.png'
import { Ellipsis, Play } from '@/assets/svg'
import { Like } from '@/assets/svg/like'
import VideoModal from '@/components/VideoModal'
import { downlad, eventTracking, Render, urlSource } from '@/libs/util'
import {
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  LoadingOutlined
} from '@ant-design/icons'

interface IProps {
  d: any
  update: () => void
  opOpen?: boolean
  checked?: boolean
  onCheckChange?: (check: boolean) => void
}

const VideoBox: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['video', 'system'])
  const { d, opOpen, checked, update, onCheckChange } = props
  const navigate = useNavigate()
  const [preview, setPreview] = useState<any>()
  const [open, setOpen] = useState(false)
  const [editData, setEditData] = useState<any>({})

  const previewVideo = (d: any) => {
    eventTracking('CreationsPlay', {
      url: d.url,
      id: d.id
    })
    setPreview({
      url: d.url,
      title: d.title,
      type: d.only_generate_audio ? 'audio' : 'video'
    })
  }

  const deleteVideo = async (id: any) => {
    Modal.confirm({
      title: translate('video.delete_title'),
      content: translate('video.delete_content'),
      okButtonProps: {
        danger: true
      },
      icon: <InfoCircleOutlined style={{ color: '#e54945' }} />,
      onOk: async () => {
        await Api.deleteCreations(id)
        update()
        message.success(translate('video.delete_success'))
      }
    })
  }

  const viewText = async (d: any) => {
    let showText
    if (d.segments?.length) {
      showText = d.segments.reduce((pre: any, next: any) => {
        return pre + (next.source_text || '')
      }, '')
    } else {
      showText = d.source_text
    }
    showText = showText.replace(/<break[^>]*><\/break>/g, '').replace(/&nbsp;|&gt;/g, '')
    Modal.confirm({
      width: 800,
      className: 'modal-source-text',
      title: null,
      content: (
        <div className="text-wrapper">
          <div className="header">
            <div className="left">{translate('video.view_dialogue')}</div>
            <div className="right">
              <CopyToClipboard
                text={showText}
                onCopy={() => {
                  message.success(translate('video.dialogue_copied'))
                }}
              >
                <CopyOutlined />
              </CopyToClipboard>

              <CloseOutlined onClick={() => Modal.destroyAll()} />
            </div>
          </div>
          <div className="content">{showText}</div>
        </div>
      ),
      icon: null,
      footer: null,
      maskClosable: true
    })
  }

  const onCreationVote = async (d: any, vote_status: number) => {
    await Api.creationVote(d.id, {
      vote_status
    })
    update()
  }

  const reCreate = async (d: any) => {
    eventTracking('CopyCreateVideo')
    const res = await Api.getCreationDetail(d.id)
    if (res?.origin_request) {
      navigate(`/create-video?id=${d.id}`)
    }
  }

  const renameVideo = (d: any) => {
    setEditData(d)
  }

  const sureRename = async () => {
    await Api.updateCreations(editData.id, {
      title: editData.title
    })
    setEditData(undefined)
    update()
  }

  return (
    <>
      <div className={`video-common-box ${open ? 'hovered' : ''}`}>
        {
          <Render>
            {function List() {
              const [imgClass, setImgClass] = useState('')
              const [ratio, setRatio] = useState<any>()
              const [percent, setPercent] = useState(-1)
              const [ref, inView] = useInView({ triggerOnce: true })
              const xhrRef = useRef<any>()

              const downladVideo = (d: any, e: any) => {
                eventTracking('CreationsDownload', {
                  url: d.url,
                  id: d.id
                })
                e.stopPropagation()
                if (xhrRef.current) {
                  xhrRef.current.abort()
                  setPercent(0)
                }
                const url = d.url.split('?')[0]
                const extension = url.substring(url.lastIndexOf('.')).toLowerCase()
                const xhr: XMLHttpRequest = downlad(d.url, `${d.title}${extension}`)
                xhr.onprogress = (e) => {
                  const p = Math.floor((e.loaded / e.total) * 100)
                  setPercent(p)
                  if (p === 100) {
                    setTimeout(() => {
                      setPercent(-1)
                    }, 300)
                  }
                }
                xhrRef.current = xhr
              }

              return (
                <div className="box-main" onClick={() => d.status === 2 && previewVideo(d)}>
                  <div className="bg"></div>

                  <div className="photo" ref={ref}>
                    {d.only_generate_audio ? (
                      <img className="img audio-bg" src={AudioBg} />
                    ) : (
                      <>
                        {inView && (
                          <>
                            <img
                              className={`img ${imgClass}`}
                              src={urlSource(d.digital_human_alpha_url || d.digital_human_url, 'video')}
                              onLoad={(e: any) => {
                                const w = e.target?.naturalWidth
                                const h = e.target?.naturalHeight
                                setRatio(w / h)
                                if (w / h > 1.6) {
                                  setImgClass('portrait')
                                }
                              }}
                            />
                            {d.background_url && ratio && (
                              <img
                                style={{
                                  width: ratio >= 16 / 9 ? '100%' : 'auto',
                                  height: ratio <= 16 / 9 ? '100%' : 'auto',
                                  aspectRatio: ratio
                                }}
                                className="img-bg"
                                src={d.background_url}
                              />
                            )}
                            <img
                              className="img shadow"
                              src={urlSource(d.background_url || d.digital_human_url, 'video')}
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>

                  {d.status === 2 && d.tip && (
                    <Tooltip placement="bottom" title={d.tip}>
                      <span className="tips">{<InfoCircleOutlined />}</span>
                    </Tooltip>
                  )}

                  {percent > -1 && (
                    <div className="mask">
                      <div className="pending">
                        <LoadingOutlined />
                        <div>
                          {translate('video.downloading')} {percent}%
                        </div>
                      </div>
                    </div>
                  )}

                  {d.status !== 2 && (
                    <div className="mask">
                      {d.status === 1 && (
                        <div className="pending">
                          <LoadingOutlined />
                          <div>{translate('video.generating')}</div>
                        </div>
                      )}

                      {d.status === 4 && (
                        <div className="pending">
                          <LoadingOutlined />
                          <div>{translate('video.in_queue')}</div>
                        </div>
                      )}

                      {d.status === 3 && (
                        <Tooltip placement="bottom" title={d.error_message || d.tip || ''}>
                          <span className="status error">
                            {translate('video.generation_failed')}
                            {d.error_message && <InfoCircleOutlined />}
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  )}

                  {!opOpen && (
                    <>
                      {d.status === 2 && percent === -1 && (
                        <div className="play">
                          <Play />
                        </div>
                      )}
                      <div className="op" onClick={(e) => e.stopPropagation()}>
                        {d.status === 2 && (
                          <div className="btn" onClick={(e) => downladVideo(d, e)}>
                            <DownloadOutlined />
                          </div>
                        )}

                        {d.status !== 1 && d.status !== 4 ? (
                          <Dropdown
                            menu={{
                              items: [
                                {
                                  key: '0',
                                  label: (
                                    <div className="dropdown-list" onClick={() => viewText(d)}>
                                      <EyeOutlined />
                                      {translate('video.view_dialogue')}
                                    </div>
                                  )
                                },
                                {
                                  key: '1',
                                  label: (
                                    <div className="dropdown-list" onClick={() => reCreate(d)}>
                                      <CopyOutlined />
                                      {translate('video.recreate')}
                                    </div>
                                  )
                                },
                                {
                                  key: '2',
                                  label: (
                                    <div className="dropdown-list" onClick={() => renameVideo(d)}>
                                      <EditOutlined />
                                      {translate('system.rename')}
                                    </div>
                                  )
                                },
                                {
                                  key: '3',
                                  label: (
                                    <div className="dropdown-list" onClick={() => deleteVideo(d.id)}>
                                      <DeleteOutlined />
                                      {translate('system.delete')}
                                    </div>
                                  )
                                }
                              ].filter((l) => {
                                if (l.key === '0') {
                                  if (d.source_text || d.segments?.some((s: any) => s.type === 2)) {
                                    return true
                                  } else {
                                    return false
                                  }
                                }
                                if (l.key === '1' && !d.replicable) {
                                  return false
                                }
                                return true
                              })
                            }}
                            placement="bottomRight"
                            open={open}
                            onOpenChange={(open: boolean) => setOpen(open)}
                          >
                            <div className="btn" onClick={(e) => e.stopPropagation()}>
                              <Ellipsis />
                            </div>
                          </Dropdown>
                        ) : (
                          <Dropdown
                            menu={{
                              items: [
                                {
                                  key: '0',
                                  label: (
                                    <div className="dropdown-list" onClick={() => reCreate(d)}>
                                      <CopyOutlined />
                                      {translate('video.recreate')}
                                    </div>
                                  )
                                }
                              ]
                            }}
                            placement="bottomRight"
                            open={open}
                            onOpenChange={(open: boolean) => setOpen(open)}
                          >
                            <div className="btn" onClick={(e) => e.stopPropagation()}>
                              <Ellipsis />
                            </div>
                          </Dropdown>
                        )}
                      </div>
                    </>
                  )}

                  {d.mode === 1 && <div className="tag">{translate('video.dark_mode')}</div>}

                  {d.only_generate_audio && (
                    <div className="tag-music">
                      <img src={IconMusic} />
                    </div>
                  )}

                  {opOpen && d.status !== 1 && d.status !== 4 && (
                    <div
                      className="check-box"
                      onClick={(e) => {
                        e.stopPropagation()
                        onCheckChange?.(!checked)
                      }}
                    >
                      <Checkbox checked={checked}></Checkbox>
                    </div>
                  )}
                </div>
              )
            }}
          </Render>
        }

        <div className="bottom">
          <div className="box-title">{d.title}</div>
          <div className="box-time">
            {dayjs(d.create_time * 1000).format('YYYY-MM-DD HH:mm')}
            <Like
              className={d.vote_status === 1 ? 'actived' : ''}
              onClick={onCreationVote.bind(this, d, d.vote_status === 1 ? 0 : 1)}
            />
            <Like
              className={`unlike ${d.vote_status === 2 ? 'actived' : ''}`}
              onClick={onCreationVote.bind(this, d, d.vote_status === 2 ? 0 : 2)}
            />
          </div>
        </div>
      </div>

      <Modal
        open={!!editData?.id}
        title={translate('system.rename')}
        okText={translate('system.confirm')}
        cancelText={translate('system.cancel')}
        onCancel={() => setEditData(undefined)}
        onOk={sureRename}
        okButtonProps={{
          disabled: !editData?.title?.trim()
        }}
      >
        <div style={{ padding: 20 }}>
          <Input
            style={{ height: 40 }}
            maxLength={20}
            value={editData?.title}
            onChange={(e) =>
              setEditData({
                ...editData,
                title: e.target.value
              })
            }
          />
        </div>
      </Modal>

      <VideoModal preview={preview} onCancel={() => setPreview(undefined)} />
    </>
  )
}

export default VideoBox
